import type { ThirdPersonCamera } from '../camera/ThirdPersonCamera';
import { BusCrash, RivalOutcome } from '../bus/BusManager';
import { BusLanding } from '../bus/busRules';
import type { BusPresentationConfig } from '../config/bus.config';
import type { DozerPresentationConfig } from '../config/dozer.config';
import type { EffectsConfig } from '../config/effects.config';
import type { EventsConfig } from '../config/events.config';
import type { HazardsConfig } from '../config/hazards.config';
import type { PlayerConfig } from '../config/player.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { HazardCue } from '../hazards/HazardModel';
import { hazardKeyZ } from '../hazards/hazardRules';
import type { Hud } from '../ui/Hud';
import { ToastPriority } from '../ui/ToastQueue';
import { clamp01 } from '../utils/math';

/** Feedback for terrain contacts and moving hazards. */
export interface WorldFeedback {
  readonly terrain: EventsConfig['terrain'];
  readonly hazards: HazardsConfig['presentation'];
  /** Runner's track distance – warnings and impacts only count near it. */
  readonly playerDistance: () => number;
  /** Dozer set piece: shake of a pushed heap, the filled pit and the hard stop; toast time of its rewards. */
  readonly dozer: Pick<DozerPresentationConfig, 'trauma' | 'toastSeconds'>;
  /** ructec-Bulli: shake of a crash, of the landings and of lost load, toast time. */
  readonly bus: Pick<BusPresentationConfig, 'trauma' | 'toastSeconds'>;
}

/**
 * Translates gameplay events into feedback (camera shake/dip, toasts).
 * Particle highlights (jump whoosh, trail, near miss, collect pop) hook in
 * here later – gameplay code stays untouched.
 */
export class EffectsManager {
  constructor(events: EventBus<GameEvents>, camera: ThirdPersonCamera, hud: Hud, config: EffectsConfig, player: PlayerConfig, world: WorldFeedback) {
    events.on('player:landed', ({ impactSpeed }) => camera.landing.land(impactSpeed));
    events.on('player:jumped', ({ double }) => {
      if (double) camera.shake.addTrauma(config.doubleJumpTrauma);
    });
    events.on('player:stumbled', () => camera.shake.addTrauma(config.stumbleTrauma));
    events.on('player:hit', ({ lifeUsed }) => {
      camera.shake.addTrauma(config.hitTrauma);
      camera.addForwardLag(-player.hitPushBack);
      if (lifeUsed) hud.showToast('−1 LEBEN', config.toastSeconds);
    });
    events.on('turn:missed', ({ lifeUsed }) => {
      camera.shake.addTrauma(config.hitTrauma);
      if (lifeUsed) hud.showToast('Abzweigung verpasst! −1 LEBEN', config.toastSeconds);
    });
    // The perfect dozer run announces its extra life itself ("PERFEKT! +1 LEBEN") – the generic notice would repeat it.
    let lifeAnnounced = false;
    events.on('score:extraLife', () => {
      if (lifeAnnounced) {
        lifeAnnounced = false;
        return;
      }
      hud.showToast('+1 LEBEN – PROST!', config.toastSeconds, ToastPriority.Info);
    });
    // Dozer set piece: rewards only (no warnings – user 2026-10-02). Heap and pit show at once like a pickup, the
    // perfect run waits behind the pit notice.
    const dozer = world.dozer;
    events.on('dozer:heapPushed', ({ ride, heaps, bottles }) => {
      camera.shake.addTrauma(dozer.trauma.heap);
      hud.showToast(`ERDHAUFEN ${heaps}/${ride.heapsTotal} +${bottles}`, dozer.toastSeconds);
    });
    events.on('dozer:pitReached', ({ filled, bottles }) => {
      camera.shake.addTrauma(filled ? dozer.trauma.fill : dozer.trauma.stop);
      if (filled) hud.showToast(`GRUBE VERFÜLLT! +${bottles}`, dozer.toastSeconds);
    });
    events.on('dozer:perfect', ({ lifeGained }) => {
      lifeAnnounced = lifeGained;
      hud.showToast(lifeGained ? 'PERFEKT! +1 LEBEN' : 'PERFEKT!', dozer.toastSeconds, ToastPriority.Info);
    });
    // ructec-Bulli: the chase is told as it happens (the rival comes, rams, is shoved off or gets past), the lost load
    // with it, a clean landing is praised, the delivery pays.
    const bus = world.bus;
    events.on('bus:mounted', () => hud.showToast('ructec-BULLI! Lass keinen vorbei – halt die Ladung!', bus.toastSeconds, ToastPriority.Info));
    events.on('bus:rivalComing', () => hud.showToast('Konkurrenz von hinten – abdrängen!', bus.toastSeconds, ToastPriority.Info));
    events.on('bus:crashed', ({ kind, airborne }) => {
      camera.shake.addTrauma(kind === BusCrash.HeadOn || airborne ? bus.trauma.crash * 1.4 : bus.trauma.crash);
      if (airborne) hud.showToast('IN DER LUFT GERAMMT! Viel Ladung weg', bus.toastSeconds);
      else if (kind === BusCrash.HeadOn) hud.showToast('FRONTAL! Ladung fliegt', bus.toastSeconds);
      else if (kind === BusCrash.Side) hud.showToast('ER DRÜCKT – dagegenlenken!', bus.toastSeconds);
      else if (kind === BusCrash.Rear) hud.showToast('RUMMS!', bus.toastSeconds);
    });
    events.on('bus:rivalHit', ({ hits, toughness }) => hud.showToast(`GETROFFEN! ${hits}/${toughness}`, bus.toastSeconds));
    events.on('bus:shoved', () => {
      camera.shake.addTrauma(bus.trauma.crash);
      hud.showToast('WEGGEDRÜCKT! Halt dagegen!', bus.toastSeconds);
    });
    events.on('bus:tipped', ({ bottles }) => {
      camera.shake.addTrauma(bus.trauma.crash * 1.6);
      hud.showToast(`UMGEKIPPT! −${bottles} Flaschen`, bus.toastSeconds);
    });
    events.on('bus:oncomingAttack', () => hud.showToast('GEGENVERKEHR hält auf dich zu – spät ausweichen!', bus.toastSeconds, ToastPriority.Info));
    events.on('bus:rivalOut', ({ outcome, bottles }) => {
      if (outcome === RivalOutcome.Shoved) hud.showToast(`VON DER STRASSE GEDRÄNGT! +${bottles} Flaschen`, bus.toastSeconds);
      else if (outcome === RivalOutcome.Crashed) hud.showToast(`DEN HAT DER GEGENVERKEHR! +${bottles} Flaschen`, bus.toastSeconds);
      else hud.showToast(`ÜBERHOLT! ${bottles} Flaschen – er bremst dich aus`, bus.toastSeconds);
    });
    events.on('bus:cargoLost', () => camera.shake.addTrauma(bus.trauma.cargo));
    events.on('bus:landed', ({ quality }) => {
      camera.shake.addTrauma(quality === BusLanding.Clean ? bus.trauma.landing : bus.trauma.rough);
      if (quality === BusLanding.Clean) hud.showToast('SAUBER GELANDET!', bus.toastSeconds, ToastPriority.Info);
      else hud.showToast(quality === BusLanding.Rough ? 'AUF ZWEI RÄDERN! Ladung fliegt' : 'SCHIEF GELANDET! Ladung rutscht', bus.toastSeconds);
    });
    events.on('bus:delivered', ({ pieces, bottles }) => hud.showToast(`LADUNG GELIEFERT! ${pieces} Teile · +${bottles} Flaschen`, bus.toastSeconds));
    // The reward shows at once (urgent), like the pickup itself – with a question pool loaded the Bau-Quiz panel
    // takes over right away (Game.startQuiz).
    events.on('collectible:golden', ({ bottleValue }) => hud.showToast(`GOLDENE FLASCHE! +${bottleValue}`, config.goldenToastSeconds));
    events.on('score:stumbleLifeLost', () => hud.showToast('Gestolpert! −1 LEBEN', config.toastSeconds));
    events.on('turbo:started', () => hud.showToast('TURBO! UNVERWUNDBAR', config.toastSeconds));
    events.on('zone:changed', ({ night }) => hud.showToast(night ? 'NACHTSCHICHT – Lichter an!' : 'TAGSCHICHT', config.toastSeconds, ToastPriority.Info));
    events.on('pursuit:caught', ({ rescued }) => {
      camera.shake.addTrauma(config.hitTrauma);
      if (rescued) hud.showToast('Gerade noch entkommen! −1 LEBEN', config.toastSeconds);
    });
    events.on('player:fell', ({ lifeUsed }) => {
      camera.shake.addTrauma(world.terrain.fallTrauma);
      if (lifeUsed) hud.showToast('Abgestürzt! −1 LEBEN', config.toastSeconds);
    });
    events.on('player:headBumped', () => camera.shake.addTrauma(world.terrain.headBumpTrauma));
    // Hazards and the collapse announce themselves by sound and motion only – no "ACHTUNG – LKW!" notices
    // (user 2026-10-02: "die Warnhinweise wie Achtung LKW usw. auch entfernen").
    events.on('hazard:cue', ({ hazard, cue }) => {
      if (cue !== HazardCue.Impact) return;
      const ahead = hazard.segment.startDistance + hazardKeyZ(hazard.definition) - world.playerDistance();
      const proximity = clamp01(1 - Math.abs(ahead) / world.hazards.impactRange);
      camera.shake.addTrauma(world.hazards.impactTrauma * proximity * proximity);
    });
  }
}
